import React, { useState, useEffect, useCallback } from "react";
import { motion } from "framer-motion";
import { getPortfolioProjectsImages } from "../services/image_services";
import { SlArrowLeft, SlArrowRight, SlClose } from "react-icons/sl";

interface Project {
  folder: string;
  name: string;
  thumbnail: string; // Lokálisan tárolt fő kép
}

const projects: Project[] = [
  {
    folder: "branzan",
    name: "Project Zalău",
    thumbnail: "/assets/images/portfolio/branzan_project.webp",
  },

  {
    folder: "egli",
    name: "Project Winter Garden - Cluj-Napoca",
    thumbnail: "/assets/images/portfolio/egli_project.webp",
  },
  {
    folder: "wood_alu_munka",
    name: "Wood-Aluminium Project - Cluj-Napoca",
    thumbnail: "/assets/images/portfolio/0.webp",
  },
  {
    folder: "oreghaz_cj",
    name: "Nursing Home Project - Cluj-Napoca",
    thumbnail: "/assets/images/portfolio/oreghaz_project.webp",
  },
  {
    folder: "hintz",
    name: "Project Hintz - Cluj-Napoca",
    thumbnail: "/assets/images/portfolio/hintz_project.webp",
  },
  {
    folder: "regi_bukarest",
    name: "Project -  București",
    thumbnail: "/assets/images/portfolio/regi_bukarest_project.webp",
  },
  {
    folder: "horea_ms",
    name: "Project Str. Horea - Tg. Mureș",
    thumbnail: "/assets/images/portfolio/horea_project.webp",
  },
  {
    folder: "cublesan",
    name: "Project Cluj-Napoca",
    thumbnail: "/assets/images/portfolio/cublesan_project.webp",
  },
  {
    folder: "caritas",
    name: "Project Caritas - Tg. Mureș",
    thumbnail: "/assets/images/portfolio/caritas_project.webp",
  },

  {
    folder: "tektum",
    name: "Project Tektum - Cluj-Napoca",
    thumbnail: "/assets/images/portfolio/tektum_project.webp",
  },
  {
    folder: "bukarest",
    name: "Project București",
    thumbnail: "/assets/images/portfolio/bukarest_project.webp",
  },
  {
    folder: "bonanza",
    name: "Pensiunea Bonanza - Rădaia, Cluj",
    thumbnail: "/assets/images/portfolio/bonanza_project.webp",
  },
];

const PortfolioGrid: React.FC = () => {
  const [selectedProject, setSelectedProject] = useState<Project | null>(null);
  const [projectImages, setProjectImages] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isZoomed, setIsZoomed] = useState(false);
  const [zoomPosition, setZoomPosition] = useState({ x: 0, y: 0 });
  const [touchStartX, setTouchStartX] = useState<number | null>(null);

  const openModal = async (project: Project) => {
    setIsLoading(true);
    setSelectedProject(project);
    const images = await getPortfolioProjectsImages(project.folder);
    setProjectImages(images);
    setCurrentIndex(0);
    setIsZoomed(false);
    setIsLoading(false);
  };

  const closeModal = () => {
    setSelectedProject(null);
    setProjectImages([]);
    setCurrentIndex(0);
  };

  // Kép váltása jobbra
  const nextImage = useCallback(() => {
    setCurrentIndex((prevIndex) => (prevIndex + 1) % projectImages.length);
    setIsZoomed(false);
  }, [projectImages.length]);

  // Kép váltása balra
  const prevImage = useCallback(() => {
    setCurrentIndex(
      (prevIndex) =>
        (prevIndex - 1 + projectImages.length) % projectImages.length
    );
    setIsZoomed(false);
  }, [projectImages.length]);

  const handleImageClick = (e: React.MouseEvent<HTMLImageElement>) => {
    if (isZoomed) {
      setIsZoomed(false);
    } else {
      const rect = e.currentTarget.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const y = e.clientY - rect.top;
      setZoomPosition({ x, y });
      setIsZoomed(true);
    }
  };

  const handleModalBackgroundClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) {
      closeModal();
    }
  };

  // Billentyűparancsok figyelése
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (!selectedProject) return;
      if (event.key === "ArrowRight") nextImage();
      if (event.key === "ArrowLeft") prevImage();
      if (event.key === "Escape") closeModal();
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [selectedProject, projectImages.length, nextImage, prevImage]);

  const handleTouchStart = (e: React.TouchEvent) => {
    setTouchStartX(e.touches[0].clientX);
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    if (touchStartX === null) return;

    const touchEndX = e.touches[0].clientX;
    const touchDiff = touchStartX - touchEndX;

    if (touchDiff > 50) {
      nextImage(); // Jobbra csúsztatás → következő kép
      setTouchStartX(null);
    } else if (touchDiff < -50) {
      prevImage(); // Balra csúsztatás → előző kép
      setTouchStartX(null);
    }
  };

  return (
    <div className="p-8">
      {/* Masonry Grid */}
      <div className="columns-1 md:columns-2 lg:columns-3 gap-12 space-y-12">
        {projects.map((project) => (
          <motion.div
            key={project.name}
            className="relative w-full h-auto cursor-pointer rounded-lg shadow-md"
            initial={{ opacity: 0 }}
            whileInView={{
              opacity: 1,
              transition: { delay: 0.2, duration: 0.5 },
            }}
            viewport={{ once: false }}
            onClick={() => openModal(project)}
          >
            {/* Image */}
            <img
              src={project.thumbnail}
              loading="lazy"
              alt={project.name}
              className="w-full h-auto rounded-lg"
            />

            {/* Dark overlay and project name on hover */}
            <div className="absolute inset-0 bg-black bg-opacity-30 rounded-lg opacity-0 hover:opacity-100 transition-opacity duration-300 flex items-end justify-around p-4 md:p-6 lg:p-8">
              <span className="text-white text-sm md:text-xl font-thin">
                {project.name}
              </span>
              <SlArrowRight className="text-white text-lg md:text-xl lg:text-2xl" />
            </div>
          </motion.div>
        ))}
      </div>

      {/* Modal */}
      {selectedProject && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-80 cursor-pointer"
          onClick={handleModalBackgroundClick}
        >
          {/* Modal content */}
          <div className="relative w-full max-w-4xl bg-black bg-opacity-0 p-6 rounded-lg z-10">
            {/* Image container */}
            <div className="flex justify-center items-center relative overflow-hidden">
              {isLoading ? (
                <div className="flex flex-col justify-center items-center">
                  <div className="w-16 h-16 border-4 border-white border-t-transparent border-solid rounded-full animate-spin"></div>
                  <p className="mt-4 text-white font-normal text-lg">
                    Loading...
                  </p>
                </div>
              ) : (
                <motion.img
                  key={currentIndex}
                  src={projectImages[currentIndex]}
                  alt={`Project ${selectedProject?.name} - ${currentIndex + 1}`}
                  className={`w-auto h-full max-h-[85vh] object-cover rounded-lg shadow-lg ${
                    isZoomed
                      ? "scale-150 cursor-zoom-out"
                      : "scale-100 cursor-zoom-in"
                  }`}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1, scale: isZoomed ? 1.5 : 1 }}
                  transition={{ duration: 0.3 }}
                  onClick={handleImageClick}
                  loading="lazy"
                  onTouchStart={handleTouchStart}
                  onTouchMove={handleTouchMove}
                  style={
                    isZoomed
                      ? {
                          transform: `scale(1.75)`,
                          transformOrigin: `${zoomPosition.x}px ${zoomPosition.y}px`,
                        }
                      : undefined
                  }
                />
              )}
            </div>
          </div>

          {/* counter */}
          <div className="absolute top-4 left-4 px-4 py-4 bg-black bg-opacity-50 text-white text-xs z-50">
            {currentIndex + 1} / {projectImages.length}
          </div>

          {/* Close Button */}
          <button
            className="absolute top-4 right-4 text-white text-4xl font-bold hover:text-gray-300 z-50"
            onClick={closeModal}
          >
            <SlClose />
          </button>

          {/* Left Arrow */}
          <button
            onClick={prevImage}
            className="absolute left-6 top-1/2 transform -translate-y-1/2 text-white text-2xl md:text-4xl lg:text-5xl font-bold hover:text-gray-300 z-50"
          >
            <SlArrowLeft />
          </button>

          {/* Right Arrow */}
          <button
            onClick={nextImage}
            className="absolute right-6 top-1/2 transform -translate-y-1/2 text-white text-2xl md:text-4xl lg:text-5xl font-bold hover:text-gray-300 z-50"
          >
            <SlArrowRight />
          </button>
        </div>
      )}
    </div>
  );
};

export default PortfolioGrid;
